import { alert, modal } from '@codeleap/modals'
import { Icon, View, Text, Button, TextInput, IconCircle, FileInput } from '@/components'
import { AppForms, theme } from '@/config'
import { useRef, useState } from 'react'
import { appStatusStore } from '@/stores'
import { APIClient } from '@/services'
import { t } from '@lingui/core/macro'
import { FileInputRef } from '@codeleap/web'
import { WebInputFile } from '@codeleap/types'
import { FileInputArea, FilePreview } from './components'

const MODAL_WIDTH = 480

export const ReportUserModal = modal<{ profileId: string }, boolean>({ id: 'report-user-modal' }).content((props) => {
  const { request, profileId } = props

  const fileInputRef = useRef<FileInputRef>(null)

  const [reported, setReported] = useState(false)

  const isValid = AppForms.reportUser.use(f => f.isValid)
  const file = AppForms.reportUser.use(f => f.values.file)

  const onClose = (success = false) => {
    request.resolve(success)
    appStatusStore.set('idle')
    setTimeout(() => AppForms.reportUser.resetValues(), 300)
  }

  const onResetFile = () => {
    AppForms.reportUser.fields.file.resetValue()
  }

  const onFileSelect = (file: WebInputFile) => {
    AppForms.reportUser.fields.file.setValue(file)
  }

  const handleReport = async () => {
    appStatusStore.set('loading')

    try {
      const formValues = AppForms.reportUser.state.value

      await APIClient.Reports.reportProfile({ ...formValues, profileId })

      setReported(true)
    } catch (error) {
      logger.log('Error reporting profile:', error)
      alert.error({ title: t`Error`, body: t`Something went wrong, please try again later` })
    } finally {
      appStatusStore.set('idle')
    }
  }

  if (reported) {
    return <View style={['column', 'gap:3', { maxWidth: MODAL_WIDTH }]}>
      <View style={['center']}>
        <IconCircle
          icon={'shield-tick'}
          size={theme.values.iconSize[5]}
          bgCircle={theme.baseColors.greenSolid50}
          bgIcon={theme.baseColors.greenSolid100}
          color={theme.baseColors.greenSolid500}
        />
      </View>

      <View style={['column', 'center', 'gap:1']}>
        <Text text={'User reported'} style={['p2', 'semiBold', { color: theme.colors.textHeadingHighlighted }]} />
        <Text text={'Thanks for your report. We’ve received your complaint and it will be reviewed internally by our team. Appropriate action will be taken to ensure a safe and respectful experience for everyone.'} style={['p3', 'regular', 'textCenter', { color: theme.colors.textParagraph }]} />
      </View>

      <Button
        debugName='report:cancel'
        text='Dismiss'
        style={['neutralSecondary', 'xx-large']}
        onPress={() => onClose(true)}
      />
    </View>
  }

  return (
    <View style={['column', 'gap:3', { maxWidth: MODAL_WIDTH }]}>
      <FileInput
        multiple={false}
        accept='.png, .jpg, .jpeg'
        ref={fileInputRef}
        onFileSelect={(f) => onFileSelect(f?.[0])}
      />

      <View style={['column', 'center', 'gap:1']}>
        <Text text={'Report user'} style={['p2', 'semiBold', { color: theme.colors.textHeadingHighlighted }]} />
        <Text text={'Are you sure you want to report this user? Reports are anonymous and help us keep conversations safe.'} style={['p3', 'regular', 'textCenter', { color: theme.colors.textParagraph }]} />
      </View>

      <TextInput
        {...AppForms.reportUser.register('description')}
      />

      <View style={['fullWidth', 'column', 'gap:1']}>
        {file ? (
          <FilePreview file={file} onRemove={onResetFile} />
        ) : (
          <FileInputArea onPress={() => fileInputRef.current?.openFilePicker?.()} />
        )}

        <View style={['fullWidth', 'row', 'gap:1']}>
          <Button
            debugName='report:cancel'
            text='Cancel'
            style={['neutralSecondary', 'xx-large', 'fullWidth']}
            onPress={onClose}
          />
          <Button
            debugName='report:report'
            text='Report'
            style={['negativePrimary', 'xx-large', 'fullWidth']}
            onPress={handleReport}
            disabled={!isValid}
          />
        </View>
      </View>
    </View>
  )
}).props({
  showClose: false,
  style: ['centered', { box: ['padding:3'] }],
})
